import {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  createContext,
  CSSProperties,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
} from 'react';

/**
 * Lets a dashboard card be dragged to a new position. The sortable state is handed to `Widget`
 * through context, because `Widget`'s box is the grid item — wrapping it in an extra element would
 * break its `grid-column` span.
 */
interface SortableWidgetState {
  setNodeRef: (element: HTMLElement | null) => void;
  style: CSSProperties;
  handleAttributes: DraggableAttributes;
  handleListeners: DraggableSyntheticListeners;
  isDragging: boolean;
}

const SortableWidgetContext = createContext<SortableWidgetState | undefined>(
  undefined
);

export const useSortableWidget = () => useContext(SortableWidgetContext);

const SortableWidget: FC<PropsWithChildren<{ id: string }>> = ({
  id,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const value = useMemo(
    (): SortableWidgetState => ({
      setNodeRef,
      style: {
        // Translate, not Transform: cards differ in height and must not be scaled while dragged.
        transform: CSS.Translate.toString(transform),
        transition,
        position: 'relative',
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.8 : undefined,
      },
      handleAttributes: attributes,
      handleListeners: listeners,
      isDragging,
    }),
    [attributes, listeners, setNodeRef, transform, transition, isDragging]
  );

  return (
    <SortableWidgetContext.Provider value={value}>
      {children}
    </SortableWidgetContext.Provider>
  );
};

export default SortableWidget;
